import { MIN_CONTRAST_RATIO } from './constants.js';
const HEX_RE = /^#([0-9a-fA-F]{6})$/;
export function hexToRgb(hex) {
    const match = HEX_RE.exec(hex.trim());
    if (!match)
        throw new TypeError(`Expected a 6-digit hex colour such as #1A2B3C, got "${hex}"`);
    const int = Number.parseInt(match[1], 16);
    return { r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff };
}
export function rgbToHex({ r, g, b }) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}
/** WCAG 2.x relative luminance. */
export function relativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    const channel = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG contrast ratio between two colours; 1 (identical) to 21 (black/white). */
export function contrastRatio(foreground, background) {
    const l1 = relativeLuminance(foreground);
    const l2 = relativeLuminance(background);
    const [lighter, darker] = l1 >= l2 ? [l1, l2] : [l2, l1];
    return (lighter + 0.05) / (darker + 0.05);
}
/** Black or white — whichever is readable on the given background. */
export function bestOnColor(background) {
    return contrastRatio('#FFFFFF', background) >= contrastRatio('#111111', background)
        ? '#FFFFFF'
        : '#111111';
}
/**
 * Checks the colour pairs the interface actually renders.
 *
 * Body text must be readable, so those pairs block publishing (docs/05
 * "contrast warnings enforced at theme-save time"). Brand-accent pairs only warn
 * (docs/04 §3 "warn if contrast < 4.5:1") — a company's real brand colour often
 * sits below AA as small text, and refusing to store it would be wrong.
 *
 * Note what is deliberately *not* checked: the tenant background against the
 * primary colour. Button labels use `bestOnColor(primary)`, never the tenant's
 * background, so that pair never appears on screen.
 */
export function checkBrandingContrast(colors) {
    const specs = [
        {
            pair: 'text-on-background',
            foreground: colors.text,
            background: colors.background,
            severity: 'blocking',
            label: 'Text colour on the background',
        },
        {
            pair: 'text-on-surface',
            foreground: colors.text,
            background: colors.surface,
            severity: 'blocking',
            label: 'Text colour on surfaces (cards, sheets, the ticket)',
        },
        {
            pair: 'primary-on-background',
            foreground: colors.primary,
            background: colors.background,
            severity: 'advisory',
            label: 'Primary colour used as link and active-tab text',
        },
        {
            pair: 'label-on-primary',
            foreground: bestOnColor(colors.primary),
            background: colors.primary,
            severity: 'advisory',
            label: 'Button label on the primary colour',
        },
    ];
    const blocking = [];
    const advisory = [];
    for (const spec of specs) {
        const ratio = Math.round(contrastRatio(spec.foreground, spec.background) * 100) / 100;
        if (ratio >= MIN_CONTRAST_RATIO)
            continue;
        const warning = {
            pair: spec.pair,
            ratio,
            required: MIN_CONTRAST_RATIO,
            severity: spec.severity,
            message: spec.severity === 'blocking'
                ? `${spec.label} has a contrast ratio of ${ratio}:1, below the ${MIN_CONTRAST_RATIO}:1 minimum. Choose a darker or lighter text colour.`
                : `${spec.label} has a contrast ratio of ${ratio}:1, below the ${MIN_CONTRAST_RATIO}:1 minimum. Small text in this colour will be hard to read.`,
        };
        (spec.severity === 'blocking' ? blocking : advisory).push(warning);
    }
    return {
        ok: blocking.length === 0,
        blocking,
        advisory,
        warnings: [...blocking, ...advisory],
    };
}
/**
 * Derives dark-mode surface/text tokens from a light palette, keeping brand hues.
 * Doc 04 §3: "dark mode derives surface/text tokens automatically".
 */
export function deriveDarkColors(colors) {
    return {
        primary: colors.primary,
        secondary: colors.secondary,
        background: '#121212',
        surface: '#1E1E1E',
        text: '#F5F5F5',
    };
}
//# sourceMappingURL=color.js.map